import { GroupBase, chakraComponents } from "chakra-react-select";
import {
  AsyncSmartSelect,
  AsyncSmartSelectProps,
} from "components/SmartSelect";
import { v1Supply } from "config";
import useTranslate from "hooks/useTranslate";
import { useMemo } from "react";
import V1Image from "components/V1Image";

type V1MultiSelectOption = {
  label: string;
  value: number;
};

type V1MultiSelectProps = Omit<
  AsyncSmartSelectProps<
    V1MultiSelectOption,
    true,
    GroupBase<V1MultiSelectOption>
  >,
  | "onChange"
  | "value"
  | "defaultOptions"
  | "loadOptions"
  | "components"
  | "blurInputOnSelect"
> & {
  tokenIds?: number[];
  firstTokenId?: number;
  lastTokenId?: number;
  optionPrefix?: string;
  onChange?: (tokenIds?: number[]) => void;
};

const generateOptions = (
  inputValue: string = "0",
  firstTokenId: number,
  lastTokenId: number,
  optionPrefix: string
): V1MultiSelectOption[] => {
  const options = [];
  const valueAsNumber = parseInt(inputValue, 10);

  if (!Number.isInteger(valueAsNumber)) {
    return [];
  }

  const generateOptionsFrom = Math.max(
    Math.min(valueAsNumber, lastTokenId),
    firstTokenId
  );

  let counter = 1;
  let tokenId = generateOptionsFrom;
  while (tokenId < lastTokenId && counter <= 4) {
    options.push({
      value: tokenId,
      label: `${optionPrefix}${tokenId}`,
    });
    tokenId = parseInt(`${generateOptionsFrom}${counter}`);
    counter++;
  }

  if (options.length <= 1) {
    counter = 1;
    tokenId = generateOptionsFrom + 1;
    while (tokenId < lastTokenId && counter <= 4) {
      options.push({
        value: tokenId,
        label: `${optionPrefix}${tokenId}`,
      });
      tokenId++;
      counter++;
    }
  }

  return options;
};

const V1MultiSelect = ({
  tokenIds,
  firstTokenId = 0,
  lastTokenId = v1Supply,
  optionPrefix = "#",
  chakraStyles = {},
  onChange,
  ...rest
}: V1MultiSelectProps) => {
  const translate = useTranslate();

  const defaultOptions = useMemo(
    () => generateOptions("0", firstTokenId, lastTokenId, optionPrefix),
    [firstTokenId, lastTokenId, optionPrefix]
  );

  return (
    <AsyncSmartSelect<V1MultiSelectOption, true, GroupBase<V1MultiSelectOption>>
      value={
        tokenIds
          ? tokenIds.map((tokenId) => ({
              value: tokenId,
              label: `#${tokenId}`,
            }))
          : undefined
      }
      size="lg"
      isMulti
      isSearchable
      isClearable
      placeholder={translate("bearSelect:placeholder")}
      chakraStyles={{
        valueContainer: (provided) => ({
          ...provided,
        }),
      }}
      components={{
        Option: ({ children, ...props }) => (
          <chakraComponents.Option {...props}>
            <V1Image
              tokenId={props.data.value!}
              size="256"
              w="8"
              h="8"
              flex="none"
              mr="3"
              borderRadius="full"
            />
            {children}
          </chakraComponents.Option>
        ),
        MultiValueContainer: ({ children, ...props }) => (
          <chakraComponents.MultiValueContainer {...props}>
            <V1Image
              tokenId={props.data.value!}
              size="256"
              w="8"
              h="8"
              flex="none"
              ml="-2"
              mr="2"
              borderRadius="full"
            />
            {children}
          </chakraComponents.MultiValueContainer>
        ),
        Input: ({ children, type, ...props }) => (
          <chakraComponents.Input
            type="number"
            min={firstTokenId}
            max={lastTokenId}
            {...props}
          />
        ),
      }}
      defaultOptions={defaultOptions}
      loadOptions={(inputValue: string) =>
        Promise.resolve(
          generateOptions(inputValue, firstTokenId, lastTokenId, optionPrefix)
        )
      }
      onChange={(options) => {
        onChange?.(options?.map((option) => option.value));
      }}
      blurInputOnSelect
      {...rest}
    />
  );
};

export default V1MultiSelect;
